import { Link } from "react-router-dom";
import { ArrowRight, Compass, Languages, Sparkles } from "lucide-react";

import { SectionIntro } from "@/components/SectionIntro";
import { pickText } from "@/lib/content";

const metricsOrder = ["entries", "sefirot", "chakras", "symbols"];

export default function HomePage({ language, summary, summaryLoading, vipAccess }) {
  const metrics = metricsOrder.map((key) => ({ key, value: summary?.counts?.[key] ?? 0 }));

  return (
    <div className="space-y-12 lg:space-y-20" data-testid="home-page">
      <section className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end" data-testid="home-hero-section">
        <div className="glass-card grain-mask overflow-hidden rounded-[2.5rem] border-primary/10 p-8 md:p-12">
          <p className="section-label" data-testid="home-hero-eyebrow">
            {pickText(language, "Temple of Man", "Tempio dell'Uomo")}
          </p>
          <h2 className="mt-5 max-w-4xl text-5xl font-light text-foreground md:text-7xl" data-testid="home-hero-title">
            {pickText(
              language,
              "A living atlas of occult anatomy, built as a temple you can read.",
              "Un atlante vivente dell'anatomia occulta, costruito come un tempio da leggere.",
            )}
          </h2>
          <p className="body-copy mt-6 max-w-2xl" data-testid="home-hero-description">
            {pickText(
              language,
              "Explore how Kabbalah, chakras, tattvas, and symbolic architecture overlap across the body. Move from crown to earth, from command to manifestation.",
              "Esplora come Qabbalah, chakra, tattva e architettura simbolica si sovrappongono attraverso il corpo. Muoviti dalla corona alla terra, dal comando alla manifestazione.",
            )}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link className="ritual-button" data-testid="home-open-atlas-link" to="/atlas">
              <Compass className="h-4 w-4" />
              {pickText(language, vipAccess ? "Enter the atlas" : "Unlock the atlas", vipAccess ? "Entra nell'atlante" : "Sblocca l'atlante")}
            </Link>
            <Link className="ghost-button" data-testid="home-open-archive-link" to={vipAccess ? "/vip-chat" : "/archive"}>
              {pickText(language, vipAccess ? "Enter V.I.P. oracle" : "Search the archive", vipAccess ? "Entra nell'oracolo V.I.P." : "Cerca nell'archivio")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1" data-testid="home-metrics-grid">
          {metrics.map((metric, index) => (
            <div
              className="glass-card rounded-[1.75rem] p-5 stagger-rise"
              data-testid={`home-metric-card-${metric.key}`}
              key={metric.key}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <p className="section-label" data-testid={`home-metric-label-${metric.key}`}>
                {metric.key}
              </p>
              <p className="mt-4 text-4xl text-foreground" data-testid={`home-metric-value-${metric.key}`}>
                {summaryLoading ? "—" : metric.value}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1fr_1fr]" data-testid="home-vip-section">
        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <p className="section-label" data-testid="home-vip-eyebrow">
            {pickText(language, "V.I.P. Gate", "Porta V.I.P.")}
          </p>
          <h3 className="mt-4 text-4xl text-foreground" data-testid="home-vip-title">
            {pickText(language, "$33 yearly access unlocks the inner chambers.", "L'accesso annuale da $33 sblocca le camere interiori.")}
          </h3>
          <p className="body-copy mt-4" data-testid="home-vip-description">
            {pickText(
              language,
              "Paid members unlock the atlas, archive, structure pages, glossary access, and the premium visually aware V.I.P. oracle.",
              "I membri paganti sbloccano l'atlante, l'archivio, le pagine della struttura, il glossario e l'oracolo V.I.P. premium visivamente consapevole.",
            )}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link className="ritual-button" data-testid="home-vip-cta-link" to="/vip-chat">
              {pickText(language, vipAccess ? "Open V.I.P. chamber" : "Unlock annual access", vipAccess ? "Apri la camera V.I.P." : "Sblocca l'accesso annuale")}
            </Link>
          </div>
        </div>
        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <p className="section-label" data-testid="home-vip-benefits-eyebrow">
            {pickText(language, "What members get", "Cosa ricevono i membri")}
          </p>
          <div className="mt-4 space-y-4 text-sm leading-7 text-slate-300" data-testid="home-vip-benefits-list">
            <p>• {pickText(language, "Email-linked paid entry before the full app opens", "Ingresso a pagamento collegato all'email prima che l'app completa si apra")}</p>
            <p>• {pickText(language, "Stripe-hosted checkout with purchase receipt", "Checkout ospitato da Stripe con ricevuta d'acquisto")}</p>
            <p>• {pickText(language, "Premium 3D-feeling oracle with style switching", "Oracolo premium con sensazione 3D e cambio di stile")}</p>
            <p>• {pickText(language, "Image-aware conversations for visual interpretation", "Conversazioni consapevoli delle immagini per interpretazione visiva")}</p>
          </div>
        </div>
      </section>

      <section className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]" data-testid="home-pillars-section">
        <SectionIntro
          description={pickText(
            language,
            "The system is arranged through pillars, zones, and chambers. It is less a diagram than a ritual operating model.",
            "Il sistema è disposto attraverso colonne, zone e camere. È meno un diagramma che un modello operativo rituale.",
          )}
          eyebrow={pickText(language, "Structural Reading", "Lettura Strutturale")}
          testId="home-pillars-intro"
          title={pickText(language, "Read the body as architecture.", "Leggi il corpo come architettura.")}
        />
        <div className="grid gap-4 md:grid-cols-3" data-testid="home-pillars-grid">
          {(summary?.pillars ?? []).map((pillar) => (
            <div className="glass-card rounded-[1.75rem] p-5" data-testid={`home-pillar-card-${pillar.slug}`} key={pillar.id}>
              <p className="section-label" data-testid={`home-pillar-title-${pillar.slug}`}>
                {pickText(language, pillar.title_en, pillar.title_it)}
              </p>
              <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`home-pillar-description-${pillar.slug}`}>
                {pickText(language, pillar.description_en, pillar.description_it)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6" data-testid="home-zones-section">
        <SectionIntro
          description={pickText(
            language,
            "The upper zone governs perception and command. The lower zone turns symbolic order into movement, appetite, and production.",
            "La zona superiore governa percezione e comando. La zona inferiore trasforma l'ordine simbolico in movimento, appetito e produzione.",
          )}
          eyebrow={pickText(language, "Zones", "Zone")}
          testId="home-zones-intro"
          title={pickText(language, "Two great chambers divide the temple.", "Due grandi camere dividono il tempio.")}
        />
        <div className="grid gap-4 lg:grid-cols-2" data-testid="home-zones-grid">
          {(summary?.zones ?? []).map((zone) => (
            <div className="glass-card rounded-[2rem] p-6 md:p-8" data-testid={`home-zone-card-${zone.slug}`} key={zone.id}>
              <p className="section-label" data-testid={`home-zone-title-${zone.slug}`}>
                {pickText(language, zone.title_en, zone.title_it)}
              </p>
              <p className="mt-4 text-3xl text-foreground" data-testid={`home-zone-heading-${zone.slug}`}>
                {pickText(language, zone.title_en, zone.title_it)}
              </p>
              <p className="body-copy mt-4" data-testid={`home-zone-description-${zone.slug}`}>
                {pickText(language, zone.description_en, zone.description_it)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]" data-testid="home-bilingual-section">
        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <div className="flex items-center gap-3 text-primary" data-testid="home-bilingual-header">
            <Languages className="h-5 w-5" />
            <p className="section-label">{pickText(language, "Bilingual Reading", "Lettura Bilingue")}</p>
          </div>
          <p className="mt-4 text-3xl text-foreground" data-testid="home-bilingual-title">
            {pickText(language, "Switch between English and Italian at any time.", "Passa in qualsiasi momento tra inglese e italiano.")}
          </p>
          <p className="body-copy mt-4" data-testid="home-bilingual-description">
            {pickText(
              language,
              "Italian ceremonial names are preserved while English explanations keep the atlas legible for wider audiences.",
              "I nomi cerimoniali italiani sono preservati mentre le spiegazioni in inglese mantengono leggibile l'atlante per un pubblico più ampio.",
            )}
          </p>
        </div>
        <div className="glass-card rounded-[2rem] p-6 md:p-8">
          <div className="flex items-center gap-3 text-primary" data-testid="home-safety-header">
            <Sparkles className="h-5 w-5" />
            <p className="section-label">{pickText(language, "Careful Symbol Handling", "Gestione Attenta dei Simboli")}</p>
          </div>
          <p className="body-copy mt-4" data-testid="home-safety-description">
            {pickText(
              language,
              "Symbols with modern harm or ambiguity are treated through abstract forms and contextual notes instead of direct visual display.",
              "I simboli con danno o ambiguità moderni sono trattati attraverso forme astratte e note contestuali invece di una visualizzazione diretta.",
            )}
          </p>
        </div>
      </section>
    </div>
  );
}